import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Workflow, Zap, CheckCircle, XCircle, Clock, 
  ArrowRight, Plus, TrendingUp, Activity 
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [recentWorkflows, setRecentWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, workflowsRes] = await Promise.all([
          axios.get(`${API}/analytics`, { withCredentials: true }),
          axios.get(`${API}/workflows`, { withCredentials: true })
        ]);
        setAnalytics(analyticsRes.data);
        setRecentWorkflows(workflowsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const stats = analytics ? [
    { 
      label: "Total Workflows", 
      value: analytics.workflows.total, 
      icon: Workflow, 
      color: "text-primary",
      bgColor: "bg-primary/10"
    },
    { 
      label: "Published", 
      value: analytics.workflows.published, 
      icon: CheckCircle, 
      color: "text-green-500",
      bgColor: "bg-green-500/10"
    },
    { 
      label: "Total Executions", 
      value: analytics.executions.total, 
      icon: Zap, 
      color: "text-blue-500",
      bgColor: "bg-blue-500/10"
    },
    { 
      label: "Success Rate", 
      value: `${analytics.executions.success_rate}%`, 
      icon: TrendingUp, 
      color: "text-amber-500",
      bgColor: "bg-amber-500/10"
    },
  ] : [];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-semibold text-foreground">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground mt-1">Here's what's happening with your workflows</p>
          </div>
          <Button 
            onClick={() => navigate('/workflows/new')}
            className="rounded-full bg-primary hover:bg-primary/90"
            data-testid="create-workflow-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Create Workflow
          </Button>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <Card key={i} className="glass-card animate-pulse">
                <CardContent className="p-6">
                  <div className="h-20 bg-muted/50 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, i) => (
              <Card key={i} className="glass-card hover:-translate-y-1 transition-transform duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{stat.label}</p>
                      <p className="text-3xl font-heading font-bold text-foreground">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                      <stat.icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent Workflows */}
          <Card className="lg:col-span-2 glass-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Recent Workflows</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/workflows')}>
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-16 bg-muted/50 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : recentWorkflows.length > 0 ? (
                <div className="space-y-3">
                  {recentWorkflows.map((workflow) => (
                    <div 
                      key={workflow.workflow_id}
                      className="flex items-center justify-between p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/workflows/${workflow.workflow_id}`)}
                      data-testid={`workflow-item-${workflow.workflow_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Workflow className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{workflow.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {workflow.nodes?.length || 0} nodes
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          workflow.status === 'published' 
                            ? 'bg-green-500/10 text-green-500' 
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {workflow.status}
                        </span>
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Workflow className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No workflows yet</p>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/workflows/new')}
                    className="rounded-full"
                  >
                    Create Your First Workflow
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="font-heading">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-start rounded-xl h-12"
                onClick={() => navigate('/workflows/new')}
                data-testid="quick-new-workflow-btn"
              >
                <Plus className="w-4 h-4 mr-3" /> New Workflow
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start rounded-xl h-12"
                onClick={() => navigate('/templates')}
                data-testid="quick-templates-btn"
              >
                <Workflow className="w-4 h-4 mr-3" /> Browse Templates
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start rounded-xl h-12"
                onClick={() => navigate('/integrations')}
                data-testid="quick-integrations-btn"
              >
                <Zap className="w-4 h-4 mr-3" /> Add Integration
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start rounded-xl h-12"
                onClick={() => navigate('/executions')}
                data-testid="quick-executions-btn"
              >
                <Activity className="w-4 h-4 mr-3" /> View Executions
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Executions */}
        {analytics && analytics.recent_executions && analytics.recent_executions.length > 0 && (
          <Card className="glass-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Recent Executions</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/executions')}>
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics.recent_executions.slice(0, 5).map((execution) => (
                  <div 
                    key={execution.execution_id}
                    className="flex items-center justify-between p-4 rounded-xl bg-muted/30"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        execution.status === 'completed' 
                          ? 'bg-green-500/10' 
                          : execution.status === 'failed'
                          ? 'bg-red-500/10'
                          : 'bg-blue-500/10'
                      }`}>
                        {execution.status === 'completed' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : execution.status === 'failed' ? (
                          <XCircle className="w-5 h-5 text-red-500" />
                        ) : (
                          <Clock className="w-5 h-5 text-blue-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{execution.execution_id}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(execution.started_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      execution.status === 'completed' 
                        ? 'bg-green-500/10 text-green-500' 
                        : execution.status === 'failed'
                        ? 'bg-red-500/10 text-red-500'
                        : 'bg-blue-500/10 text-blue-500'
                    }`}>
                      {execution.status}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
